import React, { useState, useEffect } from "react";
import { CheckCircle } from "react-feather";

import Navigation from "../components/Navigation";
import Button from "../components/Buttons";
import Card from "../components/Card";
import LoadingSpinner from "../components/LoadingSpinner";

const SubscriptionManagement = () => {
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Simulate loading delay
    setTimeout(() => setLoading(false), 1000);
  }, []);

  const handleSubscriptionSelect = (tierName) => {
    if (tierName === "enterprise") {
      alert(
        "Contact our sales team for enterprise pricing and custom features including white labeling, API access, and SSO integration."
      );
    } else {
      alert(
        `Subscription to ${tierName} plan would be processed here with Stripe integration`
      );
    }
  };

  const handleAddonSelect = (addonName) => {
    alert(`${addonName} add-on would be processed here with Stripe integration`);
  };

  // Updated subscription tiers
  const subscriptionTiers = [
    {
      id: "free",
      tier: "Free",
      price: 0,
      applications_limit: 1,
      description: "1 assessment",
      features: [
        "1 Security Assessment",
        "30+ Question Questionnaire",
        "AI-Powered Risk Analysis",
        "PDF Report Generation",
      ],
      current: true,
    },
    {
      id: "basic",
      tier: "Basic",
      price: 999,
      applications_limit: 1,
      description: "1 application",
      features: [
        "1 Application Assessment",
        "50+ Question Questionnaire",
        "AI-Powered Risk Analysis",
        "PDF Report Generation",
        "Industry Benchmarking",
        "Excel Export",
      ],
    },
    {
      id: "professional",
      tier: "Professional",
      price: 7999,
      applications_limit: 10,
      description: "10 applications",
      popular: true,
      features: [
        "10 Application Assessments",
        "50+ Question Questionnaire",
        "AI-Powered Risk Analysis",
        "PDF Report Generation",
        "Industry Benchmarking",
        "Excel Export",
        "Priority Support",
      ],
    },
    {
      id: "business",
      tier: "Business",
      price: 29999,
      applications_limit: 50,
      description: "50 applications",
      features: [
        "50 Application Assessments",
        "100+ Question Questionnaire",
        "AI-Powered Risk Analysis",
        "PDF Report Generation",
        "Industry Benchmarking",
        "Excel Export",
        "Priority Support",
        "Custom Branding",
        "Advanced Analytics",
      ],
    },
    {
      id: "enterprise",
      tier: "Enterprise",
      price: null,
      applications_limit: -1,
      description: "Contact Us",
      features: [
        "Unlimited Security Assessments",
        "100+ Question Questionnaire",
        "AI-Powered Risk Analysis",
        "PDF Report Generation",
        "Industry Benchmarking",
        "White Labeling",
        "API Access",
        "SSO Integration",
        "All Add-ons Included",
        "Dedicated Support Manager",
      ],
    },
  ];

  const addons = [
    {
      id: "monetary-analysis",
      name: "Monetary Analysis",
      price: 9999,
      description:
        "Advanced financial impact analysis with detailed cost breakdowns",
    },
    {
      id: "ransomware-analysis",
      name: "Ransomware Analysis",
      price: 9999,
      description:
        "Specialized ransomware risk assessment and prevention strategies",
    },
  ];

  if (loading) return <LoadingSpinner />;

  return (
    <div className="min-h-screen bg-theme-primary">
      <Navigation />

      <div className="max-w-7xl mx-auto py-8 px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-content-primary">
            Subscription Management
          </h1>
          <p className="mt-2 text-gray-600">
            Choose the perfect plan for your organization's security assessment
            needs
          </p>
        </div>

        {/* Main Subscription Tiers */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6 mb-12">
          {subscriptionTiers.map((tier) => (
            <Card
              key={tier.id}
              className={`relative flex flex-col ${
                tier.popular ? "border-2 border-blue-500" : ""
              }`}
            >
              {tier.popular && (
                <div className="absolute top-0 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
                  <span className="bg-blue-500 text-white px-4 py-1 rounded-full text-sm">
                    Most Popular
                  </span>
                </div>
              )}

              <div className="text-center mb-6">
                <h3 className="text-xl font-semibold text-content-primary mb-2">
                  {tier.tier}
                </h3>
                <div className="mb-4">
                  {tier.price === null ? (
                    <span className="text-2xl font-bold text-content-primary">
                      Contact Us
                    </span>
                  ) : tier.price === 0 ? (
                    <>
                      <span className="text-4xl font-bold text-content-primary">
                        Free
                      </span>
                      <span className="text-gray-600 ml-2 block text-sm">
                        / {tier.description}
                      </span>
                    </>
                  ) : (
                    <>
                      <span className="text-3xl font-bold text-content-primary">
                        ${tier.price.toLocaleString()}
                      </span>
                      <span className="text-gray-600 ml-2 block text-sm">
                        / {tier.description}
                      </span>
                    </>
                  )}
                </div>
              </div>

              {/* Features */}
              <div className="mb-6 flex-grow">
                {tier.features.map((feature, index) => (
                  <div key={index} className="flex items-center mb-2">
                    <CheckCircle className="h-4 w-4 text-green-500 mr-3 flex-shrink-0" />
                    <span className="text-sm text-gray-600">{feature}</span>
                  </div>
                ))}
              </div>

              {/* Action Button */}
              <div className="mt-auto">
                <Button
                  className="w-full"
                  variant={tier.current ? "secondary" : "primary"}
                  onClick={() => handleSubscriptionSelect(tier.tier.toLowerCase())}
                >
                  {tier.tier === "Enterprise"
                    ? "Contact Sales"
                    : tier.current
                    ? "Current Plan"
                    : "Upgrade Now"}
                </Button>
              </div>
            </Card>
          ))}
        </div>

        {/* Add-ons Section */}
        <div className="mb-12">
          <h2 className="text-2xl font-bold text-content-primary mb-6">Add-ons</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {addons.map((addon) => (
              <Card key={addon.id}>
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <h3 className="text-lg font-semibold text-content-primary mb-2">
                      {addon.name}
                    </h3>
                    <p className="text-sm text-gray-600">{addon.description}</p>
                  </div>
                  <div className="text-right">
                    <div className="text-2xl font-bold text-content-primary">
                      ${addon.price.toLocaleString()}
                    </div>
                    <div className="text-sm text-gray-600">one-time</div>
                  </div>
                </div>
                <Button
                  className="w-full"
                  onClick={() => handleAddonSelect(addon.name)}
                >
                  Add to Plan
                </Button>
              </Card>
            ))}
          </div>
        </div>

        {/* Current Usage */}
        <div className="mt-12">
          <Card>
            <h3 className="text-lg font-semibold mb-4">Current Usage</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="text-center">
                <div className="text-3xl font-bold text-blue-600 mb-2">1</div>
                <div className="text-sm text-gray-600">Assessments Used</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-green-600 mb-2">0</div>
                <div className="text-sm text-gray-600">
                  Assessments Remaining
                </div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-purple-600 mb-2">
                  Free
                </div>
                <div className="text-sm text-gray-600">Current Plan</div>
              </div>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default SubscriptionManagement;
